import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiIconComponent } from '@/ui/icon/ui-icon';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, type OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type moment from 'moment';
import type { IpeTileData, TileRange } from '../../../../interfaces/Tile.interface';
import { type IpeTileCreationOutput, IpeTileCreationPopover } from '../../../../pages/ipe-tile-creation/ipe-tile-creation';
import { LocalNumberPipe } from '../../../../pipes/localnumber.pipe';
import type { VatModes } from '../../../../providers/budgets/budgets.constants';
import { ProjectsProvider } from '../../../../providers/projects/projects.provider';
import { TilesProvider } from '../../../../providers/tiles/tiles.provider';
import { AdvizeoModalService } from '../../../../services/advizeo-modal.service';
import { EditTextComponent } from '../../../form-controls/controls/edit-text/edit-text';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./tile-ipe-value-sub-component.scss'],
    selector: 'tile-ipe-value-sub-component',
    templateUrl: 'tile-ipe-value-sub-component.html',
    imports: [EditTextComponent, FormsModule, UiIconButtonComponent, UiIconComponent, TranslatePipe, LocalNumberPipe]
})
export class TileIpeValueSubComponentComponent implements OnInit {
    private _tilesProvider = inject(TilesProvider);
    private _translateService = inject(TranslateService);
    private _advizeoModalService = inject(AdvizeoModalService);
    private _projectsProvider = inject(ProjectsProvider);

    range!: TileRange;
    _tile!: IpeTileData;
    _forceBinding: boolean = true;
    _unitToDisplay!: string;
    defaultColor: string = '#6F6F6F';

    subtitle: string = '';

    displayValue!: string;

    private _vatMode!: VatModes;

    @Input() set tileData(tileParam: IpeTileData) {
        this._tile = tileParam;
        this.initTile();
    }

    @Input() set forceBinding(forceBinding: boolean) {
        this._forceBinding = forceBinding;
        this.initTile();
    }

    @Input() dateStart!: moment.Moment;
    @Input() dateEnd!: moment.Moment;
    @Input() hasTileAdminOption!: boolean;

    @Output() deleted = new EventEmitter<void>();
    @Output() edited = new EventEmitter<IpeTileData>();

    ngOnInit() {
        this._projectsProvider
            .getVatMode$()
            .pipe(untilDestroyed(this))
            .subscribe(vatMode => {
                this._vatMode = vatMode!;
                this.initTile();
            });
    }

    editTile(event: MouseEvent) {
        event.stopPropagation();
        const popover = this._advizeoModalService.openModal(IpeTileCreationPopover, {
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            editMode: true,
            tile: this._tile
        });

        popover.afterClosed().then((data: IpeTileCreationOutput | undefined) => {
            if (data && data.edit) {
                if (data.tile && data.tile.tileType === this._tile.tileType) {
                    this._tile = data.tile;
                    this.initTile();
                } else {
                    this.edited.emit(data.tile);
                }
            }
        });
    }

    deleteTile(event: MouseEvent) {
        event.stopPropagation();
        this._advizeoModalService.openLegacyDialog({
            title: this._translateService.instant('IPE.DELETE_CONFIRMATION_TITLE'),
            message: this._translateService.instant('IPE.DELETE_CONFIRMATION_MESSAGE', { tileName: this._tile.name }),
            buttons: [
                {
                    text: this._translateService.instant('BUTTONS.CANCEL'),
                    role: 'cancel'
                },
                {
                    text: this._translateService.instant('BUTTONS.DELETE'),
                    handler: () => {
                        this.processDeleteTile();
                    }
                }
            ]
        });
    }

    processDeleteTile() {
        this._tilesProvider.deleteTile(this._tile.tileId, this._tile.buildingId).then(() => {
            this.deleted.emit();
        });
    }

    updateTileName() {
        this._tilesProvider.renameTile(this._tile.tileId, this._tile.name);
    }

    initTile() {
        if (this._tile && this._tile.description && this._tile.value && this._tile.description.ranges) {
            const tileValue = this._tile.value;
            this.subtitle = this._tilesProvider.getRangeDescription(this._tile);
            this.range = this._tile.description.ranges.find(range => range.start <= tileValue && tileValue <= range.end)!;
            this.displayValue =
                this._tile.value && Math.abs(this._tile.value) < 10 ? this._tile.value.toFixed(2) : this._tile.value.toFixed(0);
            this._unitToDisplay = this._tilesProvider.getUnitWithTypeAndIndicatorToDisplay(this._tile, true, this._vatMode);
        }
    }
}
